import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { getProperties } from '../api'; // Import getProperties
import api from '../api';
import Tooltip from './Tooltip';
import { addDocumentChangeListener, removeDocumentChangeListener, dispatchDocumentChangeEvent } from '../eventSystem';
import EditDocumentModal from './EditDocumentModal';
import { showConfirm, showAlert } from '../utils/ModalService';


interface Document {
  id: string;
  property_id: string;
  lease_id?: string;
  document_type: string;
  file_name: string;
  file_url: string;
  description?: string;
  created_at: string;
}

interface Property {
    id: string;
    custom_id?: string;
    name: string;
    address: string;
}

const formatDate = (dateStr: any) => {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString('en-US');
};

export const Documents: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [properties, setProperties] = useState<Property[]>([]); // State for properties
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null); // Changed to selectedFiles and FileList
  const [propertyId, setPropertyId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/documents');
      setDocuments(response.data);
    } catch (err: any) {
      console.error('Error fetching documents:', err.response?.data || err.message);
      setError(err.response?.data?.detail || 'Error fetching documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchProperties(); // Fetch properties on component mount

    addDocumentChangeListener(fetchDocuments);

    return () => {
      removeDocumentChangeListener(fetchDocuments);
    };
  }, []);

  const fetchProperties = async () => {
    try {
        const props = await getProperties();
        setProperties(props);
    } catch (err) {
        console.error('Error fetching properties:', err);
        setError('Could not fetch properties. Please try again later.');
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setSelectedFiles(event.target.files); // Store the entire FileList
    } else {
      setSelectedFiles(null);
    }
  };

  const handleUpload = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedFiles || selectedFiles.length === 0 || !propertyId) {
      setMessage('Please select at least one file and a property.');
      return;
    }

    setMessage('Uploading document(s)...');
    const formData = new FormData();
    formData.append('property_id', propertyId);

    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append('files', selectedFiles[i]); // Append each file under the 'files' key
    }

    try {
      const response = await api.post('/documents/bulk_upload', formData, { // Changed endpoint to bulk_upload
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setMessage(response.data.message);
      setSelectedFiles(null); // Clear selected files
      setPropertyId('');
      dispatchDocumentChangeEvent(); // Dispatch event
    } catch (error: any) {
      console.error('Error uploading document(s):', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail !== null) {
        setMessage(JSON.stringify(detail));
      } else {
        setMessage(detail || 'An error occurred during document upload.');
      }
    }
  };

  const handleViewDocument = async (documentId: string) => {
    setMessage('Opening document...');
    try {
      const response = await api.get(`/documents/${documentId}/view`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] as string }));
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      // For some browsers, we might need to actually click a link to open in new tab
      // but window.open usually works with blob URLs too.
      window.open(url, '_blank');
      setMessage('');
    } catch (error: any) {
      console.error('Error viewing document:', error.response?.data || error.message);
      setMessage('Could not open document. Please ensure you are logged in.');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    setMessage('');
    showConfirm('Confirm Action', 'Are you sure you want to delete this document?', async () => {
      try {
        await api.delete(`/documents/${documentId}`);
        setMessage('Document deleted successfully!');
        dispatchDocumentChangeEvent(); // Dispatch event
      } catch (error: any) {
        console.error('Error deleting document:', error.response?.data || error.message);
        setMessage(error.response?.data?.detail || 'An error occurred during document deletion.');
      }
    });
  };

  const handleEditDocument = (doc: Document) => {
    setEditingDocument(doc);
  };

  const handleCloseModal = () => {
    setEditingDocument(null);
  };

  if (loading) {
    return <div className="container mx-auto p-4">{t("documents.k1")}</div>;
  }

  if (error) {
    return <div className="container mx-auto p-4 text-danger">{t("documents.k2")} {error}</div>;
  }

  return (
    <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
      <EditDocumentModal document={editingDocument} onClose={handleCloseModal} />
      <div className="flex items-center mb-4">
        <h2 className="text-2xl font-bold text-text-secondary">{t("documents.k3")}</h2>
        <Tooltip text="This page is your central repository for all documents related to your properties and leases. It supports a wide range of document types, including standard PDFs, scanned PDFs, and various image formats like JPEG, PNG, and GIF. You can upload, view, edit, and delete documents, making it ideal for organizing property deeds, lease agreements, financial statements, market reports, and any other relevant files. Keeping your documents organized here ensures easy access, efficient management, and a comprehensive overview of your portfolio." />
      </div>

      {message && <p className={message.includes('successfully') ? 'text-success mb-4' : 'text-danger mb-4'}>{message}</p>}

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("documents.k4")}</h3>
      <form onSubmit={handleUpload} className="mb-6 bg-bg-secondary p-4 rounded shadow border border-border-primary">
        <div className="mb-4">
          <label htmlFor="propertyId" className="block text-text-secondary text-sm font-bold mb-2">{t("documents.k5")}</label>
          <select
            id="propertyId"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            required
            className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          >
            <option value="" disabled>{t("documents.k6")}</option>
            {properties.map(prop => (
              <option key={prop.id} value={prop.id}>
                {prop.name} - {prop.address}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="documentFile" className="block text-text-secondary text-sm font-bold mb-2">{t("documents.k7")}</label>
          <input
            type="file"
            id="documentFile"
            onChange={handleFileChange}
            multiple // Added multiple attribute
            required
            className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <button type="submit" className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
          {t("documents.k8")} </button>
      </form>

      <h3 className="text-xl font-bold mb-4 text-text-secondary">{t("documents.k9")}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.length === 0 ? (
          <p className="text-text-secondary">{t("documents.k10")}</p>
        ) : (
          documents.map((doc) => (
            <div key={doc.id} className="bg-bg-secondary p-4 rounded shadow border border-border-primary">
              <h4 className="text-lg font-semibold mb-2">{doc.file_name}</h4>
              <p className="text-text-secondary"><strong>{t("documents.k11")}</strong> {doc.document_type}</p>
              <p className="text-text-secondary"><strong>{t("documents.k12")}</strong> {properties.find(p => p.id === doc.property_id)?.custom_id || doc.property_id}</p>
              {doc.description && <p className="text-text-secondary"><strong>{t("documents.k13")}</strong> {doc.description}</p>}
              <p className="text-text-secondary"><strong>{t("documents.k14")}</strong> {formatDate(doc.created_at)}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => handleViewDocument(doc.id)} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">{t("documents.k15")}</button>
                <button onClick={() => handleEditDocument(doc)} className="bg-success text-white px-4 py-2 rounded hover:bg-success-dark">{t("documents.k16")}</button>
                <button onClick={() => handleDeleteDocument(doc.id)} className="bg-danger text-white px-4 py-2 rounded hover:bg-red-700">{t("documents.k17")}</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

